/**
 * AudioSeries.jsx — authenticated catalog of audiobook + cinematic series.
 *
 * Data remains intentionally limited to the existing /api/library response:
 * grouped series identifiers, metadata, artwork, episode counts and durations.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ChevronRight,
  Clock,
  Headphones,
  Layers,
  RotateCcw,
  Search,
  User,
} from "lucide-react";
import { authFetch } from "../utils/authUtils";
import BookCover from "../components/BookCover";
import WriterName from "../components/WriterName";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatRuntime = (totalSeconds) => {
  const seconds = Number(totalSeconds || 0);
  if (!seconds) return null;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const SeriesCard = ({ series, onOpen }) => {
  const id = series.series_id || series.content_id;
  const episodeCount = series.episode_count || series.episodes?.length || 0;
  const title = series.title || series.series_title || "";
  const author = series.author_username || series.author || "";
  const authorUserId =
    series.user_id || series.author_user_id || series.writer_user_id || null;
  const runtime = formatRuntime(
    series.total_duration_seconds || series.duration_seconds,
  );
  const cover =
    series.cover_image || series.cover_image_url || series.cover_url;
  const canOpen = Boolean(id);

  return (
    <article
      className={`group relative flex min-h-[104px] w-full gap-3 rounded-2xl border border-white/10 bg-slate-950/60 p-3 text-left shadow-[0_14px_40px_-26px_rgba(0,0,0,0.9)] transition-colors hover:border-violet-300/50 hover:bg-white/[0.055] sm:gap-4 sm:p-4 ${
        !canOpen ? "opacity-55" : ""
      }`}
      data-testid={`audio-series-card-${id || "unavailable"}`}
    >
      <button
        type="button"
        onClick={onOpen}
        disabled={!canOpen}
        aria-label={title ? `Open ${title}` : "Audio series is unavailable"}
        className="absolute inset-0 z-0 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0b14] disabled:cursor-not-allowed"
      />
      <BookCover
        book={series}
        cover={cover}
        title={title || undefined}
        author={author || undefined}
        className="pointer-events-none z-10 h-20 w-14 shrink-0 rounded-xl ring-1 ring-white/10 sm:h-24 sm:w-[68px]"
        imageClassName="transition-transform duration-300 motion-reduce:transition-none group-hover:scale-[1.03]"
      />
      <div className="pointer-events-none z-10 min-w-0 flex-1 self-center">
        <p className="mb-1 inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-fuchsia-200">
          <Layers className="h-3 w-3" aria-hidden="true" /> Series
        </p>
        {title ? (
          <h2 className="font-display text-lg font-black leading-tight text-white sm:text-xl">
            {title}
          </h2>
        ) : (
          <p className="text-sm font-semibold text-slate-300">
            Title unavailable
          </p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400">
          {author && (
            <span className="pointer-events-auto inline-flex min-w-0 items-center gap-1 text-slate-300 normal-case tracking-normal">
              <User className="h-3 w-3 shrink-0" aria-hidden="true" />
              <WriterName
                userId={authorUserId}
                name={author}
                onPlainClick={canOpen ? onOpen : undefined}
                className="truncate text-slate-200"
              />
            </span>
          )}
          <span className="tabular-nums">
            {episodeCount} {episodeCount === 1 ? "episode" : "episodes"}
          </span>
          {runtime && (
            <span className="inline-flex items-center gap-1 tabular-nums">
              <Clock className="h-3 w-3" aria-hidden="true" /> {runtime}
            </span>
          )}
        </div>
        {!canOpen && (
          <p className="mt-2 text-xs text-amber-200">
            This series cannot be opened right now.
          </p>
        )}
      </div>
      <ChevronRight
        className="pointer-events-none z-10 h-5 w-5 shrink-0 self-center text-slate-500 transition-transform duration-200 motion-reduce:transition-none group-hover:translate-x-0.5 group-hover:text-amber-200"
        aria-hidden="true"
      />
    </article>
  );
};

const CatalogLoading = () => (
  <div className="space-y-2" aria-label="Loading audio series" role="status">
    {[0, 1, 2].map((row) => (
      <div
        key={row}
        className="flex min-h-[104px] animate-pulse gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.025] p-3 sm:gap-4 sm:p-4"
      >
        <div className="h-20 w-14 shrink-0 rounded-xl bg-white/[0.08] sm:h-24 sm:w-[68px]" />
        <div className="flex flex-1 flex-col justify-center gap-2">
          <div className="h-2.5 w-20 rounded bg-white/[0.09]" />
          <div className="h-5 w-2/3 max-w-sm rounded bg-white/[0.1]" />
          <div className="h-3 w-1/2 max-w-xs rounded bg-white/[0.07]" />
        </div>
      </div>
    ))}
    <span className="sr-only">Loading audio series</span>
  </div>
);

const AudioSeries = () => {
  const navigate = useNavigate();
  const [audiobooks, setAudiobooks] = useState([]);
  const [cinematics, setCinematics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadedSections, setLoadedSections] = useState(0);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    let alive = true;
    const fetchSection = async (type) => {
      const response = await authFetch(
        `${BACKEND_URL}/api/library?book_type=${type}`,
      );
      if (!response.ok)
        throw new Error(`${type} catalog request failed (${response.status})`);
      return response.json();
    };

    setLoading(true);
    setError("");
    Promise.allSettled([fetchSection("audiobook"), fetchSection("cinematic")])
      .then(([audiobookResult, cinematicResult]) => {
        if (!alive) return;
        const audiobookAvailable = audiobookResult.status === "fulfilled";
        const cinematicAvailable = cinematicResult.status === "fulfilled";
        setAudiobooks(
          audiobookAvailable
            ? audiobookResult.value.books || audiobookResult.value.items || []
            : [],
        );
        setCinematics(
          cinematicAvailable
            ? cinematicResult.value.books || cinematicResult.value.items || []
            : [],
        );
        setLoadedSections(
          Number(audiobookAvailable) + Number(cinematicAvailable),
        );

        if (!audiobookAvailable && !cinematicAvailable) {
          setError("Audio series are unavailable right now. Please try again.");
        } else if (!audiobookAvailable) {
          setError(
            "Audiobooks are unavailable right now. Cinematic drops are still shown below.",
          );
        } else if (!cinematicAvailable) {
          setError(
            "Cinematic drops are unavailable right now. Audiobooks are still shown below.",
          );
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [requestVersion]);

  const matches = (series) => {
    if (!query) return true;
    const haystack =
      `${series.title || series.series_title || ""} ${series.author_username || series.author || ""}`.toLowerCase();
    return haystack.includes(query.toLowerCase());
  };

  const open = (series) => {
    // SeriesPage is the existing authenticated destination for grouped audio
    // content. Standalone content retains its existing content route.
    if (series.series_id) navigate(`/series/${series.series_id}`);
    else if (series.content_id) navigate(`/content/${series.content_id}`);
  };

  const filteredAudiobooks = audiobooks.filter(matches);
  const filteredCinematics = cinematics.filter(matches);
  const hasResults =
    filteredAudiobooks.length > 0 || filteredCinematics.length > 0;
  const isSearchEmpty =
    !loading && loadedSections > 0 && !hasResults && Boolean(query);
  const isCatalogEmpty =
    !loading && loadedSections > 0 && !hasResults && !query;

  return (
    <main
      className="min-h-screen bg-[radial-gradient(circle_at_top_right,rgba(124,58,237,0.18),transparent_30%),radial-gradient(circle_at_top_left,rgba(251,191,36,0.08),transparent_24%)] px-3 pb-32 pt-5 sm:px-4 sm:pt-7"
      data-testid="audio-series-page"
    >
      <div className="mx-auto max-w-3xl">
        <div className="mb-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate("/library")}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl px-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-300 transition-colors hover:bg-white/[0.06] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            data-testid="audio-series-back"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Library
          </button>
          <p className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-fuchsia-200">
            <Headphones className="h-3.5 w-3.5" aria-hidden="true" /> Listen
          </p>
        </div>

        <header className="mb-6 rounded-3xl border border-white/10 bg-slate-950/55 px-5 py-6 shadow-[0_24px_70px_-40px_rgba(0,0,0,0.95)] sm:px-7 sm:py-8">
          <p className="mb-2 text-[11px] font-black uppercase tracking-[0.2em] text-amber-200">
            Your audio library
          </p>
          <h1 className="font-display text-4xl font-black leading-[0.96] text-white sm:text-5xl">
            Audio journeys<span className="text-fuchsia-300">.</span>
          </h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Explore the audiobook and cinematic series currently available in
            your library.
          </p>
        </header>

        <label className="relative mb-6 block">
          <span className="sr-only">Search audio series or narrator</span>
          <Search
            className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/45"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search audio series or narrator"
            className="min-h-[48px] w-full rounded-2xl border border-white/10 bg-slate-950/75 py-3 pl-11 pr-4 text-sm text-white shadow-inner shadow-black/20 placeholder:text-white/35 outline-none transition-colors focus:border-violet-300/70 focus:ring-2 focus:ring-violet-300/25"
            data-testid="audio-series-search"
          />
        </label>

        {loading && <CatalogLoading />}

        {!loading && error && (
          <div
            className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-300/30 bg-amber-400/[0.08] p-4 text-sm text-amber-50"
            data-testid="audio-series-error"
            role="alert"
          >
            <AlertCircle
              className="mt-0.5 h-5 w-5 shrink-0 text-amber-200"
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <p>{error}</p>
              <button
                type="button"
                onClick={() => setRequestVersion((version) => version + 1)}
                className="mt-3 inline-flex min-h-[40px] items-center gap-2 rounded-xl border border-amber-200/40 bg-white/[0.06] px-3 text-xs font-bold text-amber-50 transition-colors hover:bg-white/[0.12] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-200"
                data-testid="audio-series-retry"
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" /> Retry
              </button>
            </div>
          </div>
        )}

        {!loading && isSearchEmpty && (
          <div
            className="rounded-2xl border border-white/10 bg-slate-950/50 p-7 text-center"
            data-testid="audio-series-search-empty"
          >
            <Search
              className="mx-auto h-6 w-6 text-slate-400"
              aria-hidden="true"
            />
            <p className="mt-3 text-sm font-bold text-white">
              No matching audio series
            </p>
            <p className="mt-1 text-sm text-slate-400">
              Try a different title or narrator.
            </p>
          </div>
        )}

        {!loading && isCatalogEmpty && (
          <div
            className="rounded-2xl border border-white/10 bg-slate-950/50 p-7 text-center"
            data-testid="audio-series-empty"
          >
            <Headphones
              className="mx-auto h-7 w-7 text-fuchsia-200"
              aria-hidden="true"
            />
            <p className="mt-3 text-sm font-bold text-white">
              No audio series are available yet.
            </p>
            <p className="mt-1 text-sm text-slate-400">
              When audio series are added to your library, they will appear
              here.
            </p>
          </div>
        )}

        {!loading && filteredAudiobooks.length > 0 && (
          <section
            className="mb-8"
            aria-labelledby="audio-series-audiobooks-heading"
            data-testid="audio-series-audiobooks"
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2
                id="audio-series-audiobooks-heading"
                className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.16em] text-fuchsia-200"
              >
                <Headphones className="h-4 w-4" aria-hidden="true" /> Audiobooks
              </h2>
              <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] font-bold tabular-nums text-slate-300">
                {filteredAudiobooks.length}
              </span>
            </div>
            <div className="space-y-2.5">
              {filteredAudiobooks.map((series) => (
                <SeriesCard
                  key={series.series_id || series.content_id}
                  series={series}
                  onOpen={() => open(series)}
                />
              ))}
            </div>
          </section>
        )}

        {!loading && filteredCinematics.length > 0 && (
          <section
            aria-labelledby="audio-series-cinematic-heading"
            data-testid="audio-series-cinematic"
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2
                id="audio-series-cinematic-heading"
                className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.16em] text-sky-200"
              >
                <Layers className="h-4 w-4" aria-hidden="true" /> Cinematic
                drops
              </h2>
              <span className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] font-bold tabular-nums text-slate-300">
                {filteredCinematics.length}
              </span>
            </div>
            <div className="space-y-2.5">
              {filteredCinematics.map((series) => (
                <SeriesCard
                  key={series.series_id || series.content_id}
                  series={series}
                  onOpen={() => open(series)}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
};

export default AudioSeries;
